import React, { useEffect } from 'react';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import Panel from '../../ui/Panel/Panel';
import useOrderTracking from '../../../hooks/useOrderTracking';
import { useTheme } from '../../../hooks/useTheme';
import Status from './components/Status';
import Stepper from './components/Stepper';
import DeliveryHistory from './components/DeliveryHistory';
import { getStepperInfo } from './utils';
import {
  Car,
  CarActive,
  CheckMark,
  CheckMarkActive,
  Error,
  House,
  HouseActive,
  Order,
  OrderActive,
  Scheduled,
  Store,
  StoreActive,
} from './components/Icons';
import TrackingNumber from './components/TrackingNumber';
import { LayoutTree } from '../DynamicLayout';
import VPOD from '../EddPanel/Vpod/Vpod';

export interface DeliveryStatusProps {
  featureName?: string;
  fixedPanelHeight?: string;
  layout?: LayoutTree.FeatureLayout;
}

const DeliveryStatus = ({
  featureName,
  fixedPanelHeight,
  layout = LayoutTree.FeatureLayout.PORTRAIT,
}: DeliveryStatusProps) => {
  const {
    panelcontainerNoneNone: containerStyle,
    panelbodyNoneNone: panelBodyStyle,
    iconsPrimaryNone,
  } = useTheme();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const logEvent = useAnalyticsPost();

  useEffect(() => {
    if (!currentShipment) return;

    logEvent({
      eventComponent: NarvarEventComponents.NTH_BLOCK_DELIVERY_STATUS,
      eventFeature: NarvarEventComponents.NTH_BLOCK_DELIVERY_STATUS,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_DELIVERY_STATUS,
    });
  }, [currentShipment, logEvent, featureName]);

  if (!currentShipment) return <></>;

  const {
    carrier,
    carrierLogoUrl,
    displayDate,
    displayDateRange,
    status,
    statusCode,
    subStatus,
    history,
    trackingNumber,
    trackingUrl,
  } = currentShipment;

  if (typeof statusCode !== 'string' || typeof status !== 'string') {
    return <></>;
  }

  const LastMileIcons = {
    activeIcons: [
      <CarActive key="mapview_car_active" style={iconsPrimaryNone} />,
      <HouseActive key="mapview_house_active" style={iconsPrimaryNone} />,
    ],
    inactiveIcons: [
      <Car key="mapview_car_inactive" style={iconsPrimaryNone} />,
      <House key="mapview_house_inactive" style={iconsPrimaryNone} />,
    ],
    error: [
      <Error key="mapview_error" style={iconsPrimaryNone} />,
      <House key="mapview_house error" style={iconsPrimaryNone} />,
    ],
  };

  const LDIcons = {
    activeIcons: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <OrderActive style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <StoreActive style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <CarActive style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <HouseActive style={iconsPrimaryNone} />,
    ],
    inactiveIcons: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Order style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Store style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Car style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <House style={iconsPrimaryNone} />,
    ],
    error: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Order style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Error style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <House style={iconsPrimaryNone} />,
    ],
  };

  const HPUIcons = {
    activeIcons: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Scheduled style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <CarActive style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <CheckMarkActive style={iconsPrimaryNone} />,
    ],
    inactiveIcons: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Scheduled style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Car style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <CheckMark style={iconsPrimaryNone} />,
    ],
    error: [
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Order style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <Error style={iconsPrimaryNone} />,
      // TODO: refactor this instead of disabling
      // eslint-disable-next-line react/jsx-key
      <House style={iconsPrimaryNone} />,
    ],
  };

  const deliveryIcons = () => {
    switch (status) {
      case 'HPUSCHEDULED':
      case 'HPUEXCEPTION':
      case 'HPUPICKUPINPROGRESS':
      case 'HPUPICKEDUP':
        return HPUIcons;
      case 'MVINTRANSIT':
      case 'MVDELIVERED':
      case 'MVEXCEPTION':
        return LastMileIcons;
      default:
        return LDIcons;
    }
  };

  return (
    <Panel
      header={<></>}
      sOuter={{
        boxSizing: 'border-box',
        ...containerStyle,
        height:
          fixedPanelHeight ||
          (containerStyle && containerStyle.height) ||
          '100%',
        overflowX: 'hidden', // Needed for the <Row /> to have negative margins without scrollbar
      }}
      sInner={{
        margin: '0rem auto',
        ...panelBodyStyle,
        height: fixedPanelHeight ? 'auto' : '100%',
        maxHeight: fixedPanelHeight ? '100%' : 'auto',
        overflowX: 'hidden', // Needed for the <Row /> to have negative margins without scrollbar
      }}
    >
      {typeof displayDate === 'string' && (
        <Status
          displayDate={displayDate}
          displayDateRange={displayDateRange}
          status={status}
          statusCode={statusCode}
          subStatus={subStatus}
        />
      )}
      <Stepper
        icons={deliveryIcons()}
        stepperColor={iconsPrimaryNone?.color as string}
        {...getStepperInfo(status, statusCode)}
      />
      <VPOD layout={layout} featureName={featureName} />
      {history?.length > 0 && <DeliveryHistory history={history} />}
      <TrackingNumber
        // `carrier` and `carrierLogoUrl` are nullable on the orderTracking query because of
        // scenarios like preship and bopis. However, it will be populated with
        // at least the generic carrier and logo url in every other case.
        carrier={carrier || ''}
        carrierLogoUrl={carrierLogoUrl || ''}
        carrierTrackingLink={trackingUrl ?? ''}
        trackingNumber={trackingNumber ?? ''}
        featureName={featureName}
      />
    </Panel>
  );
};

export default DeliveryStatus;
